import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CrmCustomFieldsFilesService,
  CustomFieldEntityType,
  FileDownloadResponse,
  FileUploadResponse,
} from 'src/api/generated';
import { ApiResponse } from 'src/hooks/api/api';

interface UseFileDownloadUrlParams {
  fileId: string;
  enabled?: boolean;
}

interface UseFileDownloadUrlResponse extends ApiResponse {
  downloadUrl: string | null;
}

/**
 * Hook for getting a download URL for a file custom field value
 */
export const useFileDownloadUrl = (
  params: UseFileDownloadUrlParams
): UseFileDownloadUrlResponse => {
  const { fileId, enabled = true } = params;

  const fetchDownloadUrl = async (): Promise<FileDownloadResponse> => {
    return await CrmCustomFieldsFilesService.getFileDownloadUrl(fileId);
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['crm/custom-fields/files/download', fileId],
    queryFn: fetchDownloadUrl,
    enabled: enabled && !!fileId,
  });

  return {
    loading,
    error,
    downloadUrl: data?.downloadUrl || null,
    refetch,
  };
};

interface UseFileUploadMutationParams {
  file: File;
  entityType: CustomFieldEntityType;
  entityId: number;
}

interface UseFileUploadParams {
  onSuccess?: (data: FileUploadResponse) => void;
  onError?: (error: Error) => void;
}

interface UseFileUploadResponse extends ApiResponse {
  uploadFile: (
    params: UseFileUploadMutationParams
  ) => Promise<FileUploadResponse>;
  data: FileUploadResponse | null;
  refetch: () => Promise<unknown>;
}

/**
 * Hook for uploading a file for a custom field
 */
export const useFileUpload = (
  params: UseFileUploadParams = {}
): UseFileUploadResponse => {
  const { onSuccess, onError } = params;

  const uploadFileMutation = async ({
    file,
    entityType,
    entityId,
  }: UseFileUploadMutationParams): Promise<FileUploadResponse> => {
    return await CrmCustomFieldsFilesService.uploadFile({
      file: file,
      entityType: entityType.toString(),
      entityId: entityId.toString(),
    });
  };

  const {
    mutateAsync,
    data,
    error,
    isPending: loading,
    reset,
  } = useMutation<FileUploadResponse, Error, UseFileUploadMutationParams>({
    mutationFn: uploadFileMutation,
    onSuccess,
    onError,
  });

  const uploadFile = async (
    params: UseFileUploadMutationParams
  ): Promise<FileUploadResponse> => {
    return await mutateAsync(params);
  };

  const refetch = async (): Promise<unknown> => {
    reset();
    return Promise.resolve(null);
  };

  return {
    uploadFile,
    data: data || null,
    loading,
    error: error || null,
    refetch,
  };
};
